/**
 * useWebSocketAlerts - Hook for real-time spending alerts via WebSocket
 * Updated to work with httpOnly cookie authentication
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { getWebSocketToken } from '../lib/api';
import logger from '../lib/logger';

const useWebSocketAlerts = (enabled = true) => {
  const [isConnected, setIsConnected] = useState(false);
  const [lastAlert, setLastAlert] = useState(null);
  const [connectionError, setConnectionError] = useState(null);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const tokenRef = useRef(null);
  const maxReconnectAttempts = 5;

  const fetchWsToken = useCallback(async () => {
    try {
      const response = await getWebSocketToken();
      tokenRef.current = response.data.token;
      return response.data.token;
    } catch (error) {
      logger.error('Failed to fetch WebSocket token:', error);
      return null;
    }
  }, []);

  const getWebSocketUrl = useCallback((token) => {
    if (!token) return null;

    // Get the backend URL and convert to WebSocket URL
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    const wsProtocol = backendUrl.startsWith('https') ? 'wss' : 'ws';
    const wsHost = backendUrl.replace(/^https?:\/\//, '');
    
    return `${wsProtocol}://${wsHost}/api/ws/alerts?token=${token}`;
  }, []);

  const handleMessage = useCallback((event) => {
    try {
      const data = JSON.parse(event.data);
      
      switch (data.type) {
        case 'connection':
          logger.log('WebSocket connected:', data.message);
          break;
          
        case 'spending_alert':
          setLastAlert(data.data);
          // Show toast notification based on alert level
          if (data.data.alert_level === 'critical') {
            toast.error(data.data.message, {
              duration: 8000,
              icon: '🚨',
              description: `${data.data.category}: $${data.data.current_spent.toFixed(2)} of $${data.data.budget_limit.toFixed(2)}`
            });
          } else {
            toast.warning(data.data.message, {
              duration: 6000,
              icon: '⚠️',
              description: `${data.data.category}: ${data.data.percent_used.toFixed(1)}% used`
            });
          }
          break;
          
        case 'transaction':
          // Show transaction notification
          toast.info(`New transaction: $${data.data.amount.toFixed(2)}`, {
            duration: 4000,
            icon: '💳',
            description: `${data.data.merchant} (${data.data.category})`
          });
          break;
          
        case 'budget_update':
          toast.success('Budget updated', {
            duration: 3000,
            icon: '📊',
            description: `${data.data.category}: $${data.data.new_budget.toFixed(2)}`
          });
          break;
          
        case 'pong':
          // Heartbeat response - connection is healthy
          break;
          
        default:
          logger.log('Unknown WebSocket message type:', data.type);
      }
    } catch (error) {
      logger.error('Error parsing WebSocket message:', error);
    }
  }, []);

  const connect = useCallback(async () => {
    if (!enabled) return;

    // Get fresh token for WebSocket connection
    const token = await fetchWsToken();
    if (!token) {
      setConnectionError('Could not authenticate');
      return;
    }

    const url = getWebSocketUrl(token);
    if (!url) return;

    try {
      // Close existing connection if any
      if (wsRef.current) {
        wsRef.current.close();
      }

      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        logger.log('WebSocket connected');
        setIsConnected(true);
        setConnectionError(null);
        reconnectAttemptsRef.current = 0;
      };

      ws.onmessage = handleMessage;

      ws.onerror = (error) => {
        logger.error('WebSocket error:', error);
        setConnectionError('Connection error');
      };

      ws.onclose = (event) => {
        logger.log('WebSocket closed:', event.code, event.reason);
        setIsConnected(false);
        wsRef.current = null;

        // Attempt to reconnect if not a clean close
        if (event.code !== 1000 && enabled && reconnectAttemptsRef.current < maxReconnectAttempts) {
          reconnectAttemptsRef.current += 1;
          const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 30000);
          logger.log(`Reconnecting in ${delay}ms (attempt ${reconnectAttemptsRef.current})`);
          
          reconnectTimeoutRef.current = setTimeout(() => {
            connect();
          }, delay);
        }
      };
    } catch (error) {
      logger.error('Failed to create WebSocket:', error);
      setConnectionError('Failed to connect');
    }
  }, [enabled, fetchWsToken, getWebSocketUrl, handleMessage]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    if (wsRef.current) {
      wsRef.current.close(1000, 'User disconnected');
      wsRef.current = null;
    }
    setIsConnected(false);
  }, []);

  const sendPing = useCallback(() => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send('ping');
    }
  }, []);

  // Connect on mount, disconnect on unmount
  useEffect(() => {
    if (enabled) {
      connect();
    }

    return () => {
      disconnect();
    };
  }, [enabled, connect, disconnect]);

  // Heartbeat to keep connection alive
  useEffect(() => {
    if (!isConnected) return;

    const heartbeat = setInterval(() => {
      sendPing();
    }, 30000); // Every 30 seconds

    return () => clearInterval(heartbeat);
  }, [isConnected, sendPing]);

  // Refresh token before it expires (every 4 minutes for 5-minute tokens)
  useEffect(() => {
    if (!isConnected) return;

    const tokenRefresh = setInterval(async () => {
      const newToken = await fetchWsToken();
      if (newToken) {
        tokenRef.current = newToken;
        logger.log('WebSocket token refreshed');
      }
    }, 4 * 60 * 1000); // Every 4 minutes

    return () => clearInterval(tokenRefresh);
  }, [isConnected, fetchWsToken]);

  return {
    isConnected,
    lastAlert,
    connectionError,
    connect,
    disconnect,
    sendPing
  };
};

export default useWebSocketAlerts;
